import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useGlobalState } from "providers/globalState"
import { getSiteByLocale } from "services/sites"
import { convertFlatMenuToStructuredTree } from "util/helpers"
import { cmsRequest } from "services/craft/craft"
import auxiliaryMenuQuery from "services/craft/queries/auxiliaryMenu"
import {
    Flex,
    Box,
    Link,
    Icon,
    useDisclosure,
    useBreakpointValue,
    Image,
    Heading,
    // IconButton,
    Grid,
    GridItem,
    useMediaQuery,
} from "@chakra-ui/react"
import { FiX, FiMenu } from "react-icons/fi"
import { useFeatureValue } from "@growthbook/growthbook-react"
import CrfStripe from "components/header/crfStripe"
import MatrixBlockRenderer from "components/matrix/matrixBlockRenderer"
import Nav from "components/nav/nav"
import Wrapper from "components/layout/wrapper"
// import { CalendarIcon } from "components/graphics/calendarIcon"
import AuxiliaryMenu from "components/layouts/tct2026/AuxiliaryMenu"
import { arrangeNav } from "lib/growthbook/arrange-nav"

export default function HeaderTct2026({ pageTopContent, pageTitle, site, hideNav = false, onViewportChange }) {
    const { locale } = useGlobalState()
    const [auxiliaryItems, setAuxiliaryItems] = useState([])
    const [auxiliaryLoaded, setAuxiliaryLoaded] = useState(false)

    useEffect(() => {
        const siteConfig = getSiteByLocale(locale)
        if (!siteConfig?.craftId) {
            setAuxiliaryLoaded(true)
            return
        }

        cmsRequest(auxiliaryMenuQuery, { siteId: siteConfig.craftId })
            .then(data => {
                if (data?.flatmenu) {
                    setAuxiliaryItems(convertFlatMenuToStructuredTree([...data.flatmenu]))
                }
            })
            .catch(err => {
                console.error("Failed to fetch auxiliary menu:", err)
            })
            .finally(() => {
                setAuxiliaryLoaded(true)
            })
    }, [locale])

    const navConfig = useFeatureValue("tct2026-nav-arrangement", { moves: [] })
    const hasMoves = navConfig.moves.length > 0
    const { main: mainMenu, auxiliary: arrangedAuxiliaryItems } = arrangeNav(site.menu, auxiliaryItems, navConfig.moves)
    const navReady = !hasMoves || auxiliaryLoaded

    const { open, onToggle } = useDisclosure({ defaultOpen: false })
    const isNavCollapsed = useBreakpointValue({ base: true, menu: false }, { fallback: "menu" })
    const [isMobile] = useMediaQuery("(max-width: 600px)")

    const isSmall = useBreakpointValue({ base: true, sm: false }, { fallback: "sm" })
    const [menuIsTooWideForWindow, setMenuIsTooWideForWindow] = useState(false)
    const [isScrolled, setIsScrolled] = useState(false)
    // Natural (in-flow) height of the header bar, used to size the spacer that
    // reserves its space once the bar switches to position:fixed on scroll.
    const [barHeight, setBarHeight] = useState(0)
    const showMobileMenu = useMemo(() => {
        return isNavCollapsed || menuIsTooWideForWindow
    }, [isNavCollapsed, menuIsTooWideForWindow])
    const crfStripeHeight = useMemo(() => {
        return isNavCollapsed || site.theme.hideCrfTopBar ? 0 : 42
    }, [isNavCollapsed])
    // The fixed/pinned bar is desktop-only — never while the mobile menu is in use.
    const isFixed = isScrolled && !showMobileMenu
    const banner = useRef()

    useEffect(() => {
        if (open && showMobileMenu) {
            document.body.classList.add("menu-isOpen")
        } else {
            document.body.classList.remove("menu-isOpen")
        }

        // onViewportChange && onViewportChange(showMobileMenu, showMobileMenu ? crfStripeHeight + "px" : "62px")
    }, [open, showMobileMenu])

    const onScroll = useCallback(event => {
        // Trigger fixed header after minimal scroll
        setIsScrolled(window.scrollY > 50)
    }, [])

    useEffect(() => {
        window.addEventListener("scroll", onScroll)
        return () => {
            window.removeEventListener("scroll", onScroll)
        }
    }, [])

    // Measure the header bar's natural height so the spacer can reserve its
    // footprint when it becomes fixed. Padding doesn't change on scroll, so this
    // is stable regardless of scroll state; re-measure on resize / layout mode.
    useEffect(() => {
        const measure = () => {
            if (banner.current) setBarHeight(banner.current.offsetHeight)
        }
        measure()
        window.addEventListener("resize", measure)
        return () => window.removeEventListener("resize", measure)
    }, [showMobileMenu])

    // Extract backgroundImage and create new object without it
    const { backgroundImage, backgroundImageMobile, video2, mobileVideo2, ...pageContentWithoutBackground } =
        pageTopContent
    const [topBackgroundImage] = isSmall ? backgroundImageMobile || [] : backgroundImage || []
    const mobileLogo = site.theme?.headerLockupMobile[0]?.url
        ? site.theme?.headerLockupMobile[0]?.url
        : site.theme?.headerLockup[0]?.url

    const content = (
        <>
            {!isNavCollapsed && !site.theme.hideCrfTopBar && <CrfStripe />}
            {/* Combined Logo and Menu Bar */}
            <Box
                ref={banner}
                className={isFixed ? "siteHeader__content isFixed" : "siteHeader__content"}
                // Marks the bar as pinnable (desktop only) so ScrollToHash knows to
                // offset anchors by its height
                data-fixed-eligible={showMobileMenu ? undefined : "true"}
                position={isFixed ? "fixed" : "relative"}
                top={isFixed ? { base: 0, md: `${crfStripeHeight}px` } : "unset"}
                left={isFixed ? 0 : "unset"}
                w="100%"
                zIndex={1200}
                transition="background 0.3s ease"
                p={isFixed ? "10px" : showMobileMenu ? "15px" : "40px"}
                mt={isFixed ? 0 : { base: 0, md: `${crfStripeHeight}px` }}
                bgColor={isFixed ? "#000" : "transparent"}
                bgImage="none"
                bgSize="cover"
                bgPosition="center"
                bgRepeat="no-repeat"
                backdropFilter="none"
                boxShadow={isFixed ? "0 4px 12px rgba(0, 0, 0, 0.35)" : "none"}
            >
                <Flex
                    className="siteHeader__inner"
                    maxW="1500px"
                    mx="auto"
                    align="flex-start"
                    justify="space-between"
                    flexDirection="column"
                    gap={4}
                    position="relative"
                >
                    {/* Top Row: Logo and Auxiliary Menu */}
                    <Flex w="100%" align="center" justify="space-between">
                        {/* Logo */}
                        <Flex alignItems="center">
                            <Link href={site.meeting.homeUrl || "/"}>
                                <Image
                                    className={"heroLogo"}
                                    src={isNavCollapsed ? mobileLogo : site.theme?.headerLockup[0]?.url}
                                    alt={"meeting logo"}
                                    height={{ base: "40px", md: isFixed ? "70px" : "90px" }}
                                    width="auto"
                                />
                            </Link>
                        </Flex>

                        <Flex
                            data-gb-experiment
                            gap={{ base: 4, md: 2 }}
                            display="flex"
                            visibility={navReady ? "visible" : "hidden"}
                            // display={{ base: "none", md: "flex" }}
                            alignItems="end"
                            direction="column"
                        >
                            {/* Auxiliary Menu */}
                            {!showMobileMenu && (
                                <Box display={{ base: "none", lg: "block" }}>
                                    <AuxiliaryMenu items={arrangedAuxiliaryItems} />
                                </Box>
                            )}

                            {/* Bottom Row: Desktop Navigation */}
                            {!hideNav && (
                                <Box
                                    className="menu"
                                    display={showMobileMenu ? (open ? "block" : "none") : "flex"}
                                    position={showMobileMenu ? "fixed" : "unset"}
                                    top={showMobileMenu ? 0 : "unset"}
                                    left={showMobileMenu ? 0 : "unset"}
                                    h={showMobileMenu ? "100vh" : "auto"}
                                    w={showMobileMenu ? "100%" : "auto"}
                                    pb={showMobileMenu ? "120px" : 0}
                                    overflowY={showMobileMenu ? "scroll" : "unset"}
                                    bg={showMobileMenu ? "white" : "transparent"}
                                    zIndex={showMobileMenu ? 1300 : "auto"}
                                    alignSelf="flex-end"
                                    alignItems="center"
                                >
                                    {showMobileMenu && (
                                        <Flex
                                            className="menu__closeBtn"
                                            justify="flex-end"
                                            h="62px"
                                            alignItems="center"
                                            px="15px"
                                            bg="white"
                                            borderBottom="1px solid #e0e0e0"
                                        >
                                            <Icon
                                                as={FiX}
                                                color="#333"
                                                cursor="pointer"
                                                onClick={onToggle}
                                                w="24px"
                                                h="24px"
                                            />
                                        </Flex>
                                    )}
                                    <Nav
                                        menu={mainMenu}
                                        sessionTypeTerm={site.meeting.sessionTypeTerm}
                                        showMobileMenu={showMobileMenu}
                                        mobileMenuToggle={onToggle}
                                        setMenuIsTooWideForWindow={setMenuIsTooWideForWindow}
                                        auxiliaryMenu={
                                            showMobileMenu ? (
                                                <AuxiliaryMenu isMobile={true} items={arrangedAuxiliaryItems} />
                                            ) : null
                                        }
                                    />
                                    {/*
                                    <IconButton
                                        display={showMobileMenu ? "none" : "inline-flex"}
                                        ml={4}
                                        color="#fff"
                                        bg="var(--button-bgcolor)"
                                        p={"0px 10px"}
                                        fontSize="13px"
                                        fontWeight={500}
                                        onClick={() => {
                                            window.open("https://www.addevent.com/event/vL26621468", "_blank")
                                        }}
                                    >
                                        ADD TO CALENDAR <CalendarIcon fill="#fff" />
                                    </IconButton>
                                    */}
                                </Box>
                            )}
                        </Flex>

                        {/* Mobile Menu Button */}
                        <Flex
                            className="menu__menuBtn"
                            display={showMobileMenu ? "flex" : "none"}
                            justifyContent="center"
                            alignItems="center"
                            alignSelf={showMobileMenu ? "flex-start" : null}
                        >
                            <Icon
                                as={open ? FiX : FiMenu}
                                color="#fff"
                                cursor="pointer"
                                onClick={onToggle}
                                w="24px"
                                h="24px"
                            />
                        </Flex>
                    </Flex>
                </Flex>
            </Box>

            {/* Reserve the bar's footprint (bar height + CRF stripe offset on desktop)
                while it is fixed, so page content doesn't jump up on scroll. */}
            {isFixed && <Box aria-hidden h={{ base: `${barHeight}px`, md: `${barHeight + crfStripeHeight}px` }} />}

            {pageTitle && (
                <Wrapper id="pageHeader" pt={["20px", "10px", "0px"]}>
                    <Heading as="h1" color="#fff" fontWeight="600" dangerouslySetInnerHTML={{ __html: pageTitle }} />
                </Wrapper>
            )}
            <Box pb={video2?.[0]?.url ? (isNavCollapsed ? 10 : 0) : 10}>
                <MatrixBlockRenderer blocks={[pageContentWithoutBackground]} site={site} />
            </Box>
        </>
    )

    const siteHeaderClass = showMobileMenu ? "siteHeader menuIsMobile" : "siteHeader"

    // Which background media backs the hero, chosen by viewport + available assets:
    //  - wide desktop + desktop video    → desktop video (capped at 650px)
    //  - phone + mobile video            → mobile video (full height)
    //  - tablet range, or no video asset → static background image
    const useDesktopVideo = !isNavCollapsed && Boolean(video2?.[0]?.url)
    const useMobileVideo = !useDesktopVideo && !(isNavCollapsed && !isMobile) && Boolean(mobileVideo2?.[0]?.url)
    const heroVideoProps = useDesktopVideo
        ? { src: video2[0].url, maxHeight: "650px" }
        : useMobileVideo
          ? { src: mobileVideo2[0].url, height: "100%" }
          : null

    if (heroVideoProps) {
        return (
            <Grid templateColumns="1fr" templateRows="1fr" className={siteHeaderClass}>
                <GridItem gridColumnStart={1} gridColumnEnd={2} gridRowStart={1} gridRowEnd={2} zIndex={2}>
                    {content}
                </GridItem>
                <GridItem gridColumnStart={1} gridColumnEnd={2} gridRowStart={1} gridRowEnd={2} zIndex={1}>
                    <Box
                        className="video"
                        as="video"
                        width="100%"
                        objectFit="cover"
                        autoPlay
                        muted
                        playsInline
                        loop
                        {...heroVideoProps}
                    />
                </GridItem>
            </Grid>
        )
    }

    return (
        <Box
            className={siteHeaderClass}
            zIndex={1200}
            bgColor="#000"
            bgImage={`url(${topBackgroundImage?.url})`}
            bgSize="cover"
            bgRepeat="no-repeat"
            bgPosition={{ base: "bottom center", xl: "top right" }}
        >
            {content}
        </Box>
    )
}
